/**
 * Zustand store for Shipping Options + Category Assignments.
 * Replaces src/data/shippingStore.ts (which now re-exports from here).
 */
import { create } from "zustand";
import { devtools } from "zustand/middleware";

export interface ShippingOption {
  id: string;
  label: string;
  description: string;
  fee: number;
  enabled: boolean;
  sortOrder: number;
  /** Optional tracking preset auto-applied when an order using this option is Confirmed. */
  linkedTrackingPresetId?: string;
}

export type ShippingSortMode = "fee_asc" | "fee_desc" | "label_asc";
export type CategoryShippingMap = Record<string, string[]>;

const SHIPPING_KEY = "khays_shipping_options";
const ASSIGNMENTS_KEY = "khays_shipping_category_assignments";

function normalizeSortOrder(options: ShippingOption[]): ShippingOption[] {
  return options.map((option, index) => ({ ...option, sortOrder: index }));
}

function getDefaultShippingOptions(): ShippingOption[] {
  return [
    { id: "default", label: "Standard Shipping", description: "Regular delivery across Ghana (3-5 business days)", fee: 500, enabled: true, sortOrder: 0 },
  ];
}

function loadOptionsFromStorage(): ShippingOption[] {
  try {
    const stored = localStorage.getItem(SHIPPING_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as ShippingOption[];
      const cleaned = parsed.map(({ categoryRestrictions, ...rest }: any) => rest);
      return normalizeSortOrder(cleaned);
    }
  } catch {}
  return getDefaultShippingOptions();
}

function loadMapFromStorage(): CategoryShippingMap {
  try {
    const stored = localStorage.getItem(ASSIGNMENTS_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  // Migrate from legacy categoryRestrictions on options
  const map: CategoryShippingMap = {};
  try {
    const stored = localStorage.getItem(SHIPPING_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as any[];
      parsed.forEach(opt => {
        if (opt.categoryRestrictions && opt.categoryRestrictions.length > 0) {
          opt.categoryRestrictions.forEach((cat: string) => {
            if (!map[cat]) map[cat] = [];
            if (!map[cat].includes(opt.id)) map[cat].push(opt.id);
          });
        }
      });
    }
  } catch {}
  try { localStorage.setItem(ASSIGNMENTS_KEY, JSON.stringify(map)); } catch {}
  return map;
}

interface ShippingState {
  options: ShippingOption[];
  map: CategoryShippingMap;
  _rev: number;
  setOptions: (options: ShippingOption[]) => void;
  setMap: (map: CategoryShippingMap) => void;
  reload: () => void;
}

export const useShippingStore = create<ShippingState>()(
  devtools(
    (set, get) => ({
      options: loadOptionsFromStorage(),
      map: loadMapFromStorage(),
      _rev: 0,
      setOptions: (options) => {
        const normalized = normalizeSortOrder(options);
        localStorage.setItem(SHIPPING_KEY, JSON.stringify(normalized));
        set({ options: normalized, _rev: get()._rev + 1 });
      },
      setMap: (map) => {
        localStorage.setItem(ASSIGNMENTS_KEY, JSON.stringify(map));
        set({ map, _rev: get()._rev + 1 });
      },
      reload: () => set({
        options: loadOptionsFromStorage(),
        map: loadMapFromStorage(),
        _rev: get()._rev + 1,
      }),
    }),
    { name: "ShippingStore" }
  )
);

// ─── Functional API (used across the codebase) ─────────────────

export function loadShippingOptions(): ShippingOption[] {
  return useShippingStore.getState().options;
}

export function saveShippingOptions(options: ShippingOption[]): void {
  useShippingStore.getState().setOptions(options);
}

export function loadCategoryShippingMap(): CategoryShippingMap {
  return useShippingStore.getState().map;
}

export function saveCategoryShippingMap(map: CategoryShippingMap): void {
  useShippingStore.getState().setMap(map);
}

export function toggleCategoryShipping(category: string, optionId: string): CategoryShippingMap {
  const map = { ...useShippingStore.getState().map };
  const current = map[category] || [];
  if (current.includes(optionId)) {
    map[category] = current.filter(id => id !== optionId);
    if (map[category].length === 0) delete map[category];
  } else {
    map[category] = [...current, optionId];
  }
  useShippingStore.getState().setMap(map);
  return map;
}

export function getCategoryAssignments(category: string): string[] {
  return useShippingStore.getState().map[category] || [];
}

export function sortShippingOptions(mode: ShippingSortMode): ShippingOption[] {
  const options = [...useShippingStore.getState().options].sort((a, b) => {
    if (mode === "fee_asc") return a.fee - b.fee;
    if (mode === "fee_desc") return b.fee - a.fee;
    return a.label.localeCompare(b.label);
  });
  useShippingStore.getState().setOptions(options);
  return useShippingStore.getState().options;
}

export function addShippingOption(option: Omit<ShippingOption, "id" | "sortOrder">): ShippingOption {
  const options = useShippingStore.getState().options;
  const newOption: ShippingOption = { ...option, id: `ship_${Date.now()}`, sortOrder: options.length };
  useShippingStore.getState().setOptions([...options, newOption]);
  return newOption;
}

export function updateShippingOption(id: string, updates: Partial<ShippingOption>): void {
  const options = useShippingStore.getState().options.map(o =>
    o.id === id ? { ...o, ...updates } : o
  );
  useShippingStore.getState().setOptions(options);
}

export function removeShippingOption(id: string): void {
  const options = useShippingStore.getState().options.filter(o => o.id !== id);
  useShippingStore.getState().setOptions(options);

  // Also clean up any category assignments referencing this option
  const map = { ...useShippingStore.getState().map };
  let changed = false;
  for (const cat in map) {
    if (map[cat].includes(id)) {
      map[cat] = map[cat].filter(oid => oid !== id);
      if (map[cat].length === 0) delete map[cat];
      changed = true;
    }
  }
  if (changed) useShippingStore.getState().setMap(map);
}

export function getEnabledShippingOptions(category?: string): ShippingOption[] {
  const all = useShippingStore.getState().options.filter(o => o.enabled);
  if (!category) return all;
  const assignments = useShippingStore.getState().map[category] || [];
  if (assignments.length > 0) {
    return all.filter(o => assignments.includes(o.id));
  }
  return all;
}
